import { useState, useRef } from "react";
import { toast } from "sonner";
import { resolveDisplay, contrastOf, type ColorFormats } from "./colorUtils";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { copyText } from "@/lib/copyText";

const TOOL_INFO_CONTENT = {
  what: `Converts a color between HEX, RGB, HSL, HSV, HWB, CMYK, LAB and OKLCH, and shows the nearest CSS color name, since different design tools and code contexts expect different color formats.`,
  how: `Enter a color in any supported format (HEX, rgb(), hsl(), oklch() or a name), and its equivalent values in the other formats are displayed automatically. If what you type isn't a color, the last good result stays on screen with a message. If a value was out of range, such as rgb(300, 0, 0), a note says what it was clamped to.`,
  tip: `HSL is often the easiest format to work with when you want to tweak a color's lightness or saturation without changing its underlying hue — adjust one HSL value and convert back to hex when you're happy with it.`,
};

export default function ColorConverter() {
  const [input, setInput] = useState("#6366f1");
  // A typo must not blank the page or turn the swatch black: keep showing the last readable colour.
  const lastGood = useRef<ColorFormats | null>(null);
  const { shown: f, invalid, notices, last } = resolveDisplay(input, lastGood.current);
  lastGood.current = last;
  const parsed = !invalid && input.trim() !== "" ? f : null;

  const { blocked } = useLogToolUsage(!!parsed, 3000, { ownActions: true });

  const rows: [string, string | undefined][] = [
    ["HEX", f?.hex],
    ["RGB", f?.rgb],
    ["HSL", f?.hsl],
    ["HSV / HSB", f?.hsv],
    ["HWB", f?.hwb],
    ["CMYK (screen estimate)", f?.cmyk],
    ["LAB (D50, as in CSS lab())", f?.lab],
    ["OKLCH", f?.oklch],
    ["Nearest CSS name", f?.name],
    ["CSS", f?.css],
  ];

  return (
    <ToolContainer>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-3">
          <div>
            <Label>Color (HEX, rgb(), hsl(), or a name like tomato)</Label>
            <div className="flex gap-2">
              <Input value={input} onChange={e => setInput(e.target.value)} placeholder="#6366f1" className="font-mono" aria-invalid={invalid} />
              <input type="color" aria-label="Pick a color" value={f ? f.hex.slice(0, 7) : "#000000"} onChange={e => setInput(e.target.value)} className="h-9 w-12 rounded border bg-transparent p-1" />
            </div>
            {invalid && <p role="alert" className="text-sm text-destructive mt-1">Not a valid colour. Try #ff6600, rgb(255, 102, 0), hsl(24, 100%, 50%), oklch(70% 0.2 50) or a name like orange.{f ? ` Still showing the last valid colour, ${f.hex}.` : ""}</p>}
            {notices.length > 0 && <p role="status" className="text-sm text-amber-700 dark:text-amber-400 mt-1">{notices.join(" ")}</p>}
          </div>
          {rows.map(([label, value]) => (
            <div key={label}>
              <Label className="text-xs text-muted-foreground">{label}</Label>
              <div className="flex gap-2">
                <Input readOnly aria-label={label} value={blocked ? "—" : value ?? ""} className={`font-mono ${invalid ? "opacity-60" : ""}`} />
                <button
                  type="button"
                  className="text-xs text-primary hover:underline shrink-0 disabled:opacity-40"
                  disabled={!value || blocked}
                  aria-label={`Copy ${label}`}
                  onClick={() => { void copyText(value ?? "", "Copied"); }}
                >Copy</button>
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-col items-center justify-center gap-4">
          <div className={`w-48 h-48 rounded-2xl border shadow-lg ${invalid ? "opacity-60" : ""}`} style={{ backgroundColor: f?.hex ?? "transparent" }} />
          {f && !blocked && (
            <dl className="text-xs text-muted-foreground space-y-1" data-testid="cc-contrast">
              {([["white", "#ffffff"], ["black", "#000000"]] as const).map(([name, hex]) => {
                const r = contrastOf(f.hex, hex) ?? 0;
                return <div key={name} className="flex gap-2"><dt>Contrast on {name}:</dt><dd className="font-mono">{r.toFixed(2)}:1 ({r >= 7 ? "AAA" : r >= 4.5 ? "AA" : r >= 3 ? "AA large text only" : "fails AA"})</dd></div>;
              })}
            </dl>
          )}
        </div>
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
